'use client';

import { Divider } from '@/components/divider';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import {
  SwdAccordionHeader,
  SwdAccordionItem,
  SwdAccordionPanel,
  SwdAccordionRoot,
  SwdAccordionTrigger,
} from '@/components/swdAccordion/SwdAccordion';
import { getFullSlugFromMultilink } from '@/helper/urls';
import { resolveStory } from '@/helper/utils';
import Badges from '@/storyblok/footer/components/Badges';
import Contact from '@/storyblok/footer/components/Contact';
import FooterMeta from '@/storyblok/footer/components/FooterMeta';
import TopSection from '@/storyblok/footer/components/TopSection';
import NewsletterTile from '@/storyblok/newsletterTile/NewsletterTile';
import type {
  GlobalFooterStoryblok,
  GlobalNewsletterTileStoryblok,
  PartialLinkListStoryblok,
  PartialLinkStoryblok,
} from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import React from 'react';

import classes from './Footer.module.scss';

function FooterNavLinks({ linkList, linkClassName }: { linkList: PartialLinkListStoryblok; linkClassName: string }) {
  return (
    <ul aria-labelledby={`navigation-block-heading-${linkList._uid}`}>
      {linkList.links.map((listItem: PartialLinkStoryblok, index: number) => (
        <li key={listItem._uid} {...storyblokEditable(listItem)}>
          <InlineTextLink
            data-testid={'footer-links-list-link' + (index + 1)}
            link={listItem.link}
            text={listItem.label}
            className={linkClassName}
          />
        </li>
      ))}
    </ul>
  );
}

const Footer: React.FC<{
  blok: GlobalFooterStoryblok;
  hideNewsletter?: boolean;
  hideNavigation?: boolean;
}> = ({ blok, hideNewsletter = false, hideNavigation = false }) => {
  const newsletterStory = React.useMemo(
    () => resolveStory<GlobalNewsletterTileStoryblok>(blok.newsletter_story),
    [blok.newsletter_story]
  );

  return (
    <>
      {newsletterStory && !hideNewsletter && (
        <div className={classNames(classes.newsletterWrapper, 'grid-wrapper')}>
          <NewsletterTile blok={newsletterStory.content} />
        </div>
      )}
      <footer className={classes.footer} {...storyblokEditable(blok)}>
        <div className={classes.main}>
          <TopSection blok={blok} />
          {!hideNavigation && (
            <>
              <div className={classes.navigationDesktop}>
                {blok.main_links.map((linkList) => (
                  <div key={`navigation-block-${linkList._uid}`} className={classes.navBlock}>
                    <h3 id={`navigation-block-heading-${linkList._uid}`} className={classes.heading}>
                      {linkList.title}
                    </h3>
                    <FooterNavLinks linkList={linkList} linkClassName={classes.link} />
                  </div>
                ))}
              </div>
              <div className={classes.navigationMobile}>
                {blok.main_links.map((linkList) => (
                  <SwdAccordionRoot key={linkList._uid} variant="footer" defaultValue={[]}>
                    <SwdAccordionItem value={linkList._uid}>
                      <SwdAccordionHeader id={`navigation-block-heading-${linkList._uid}`}>
                        <SwdAccordionTrigger>
                          <p>{linkList.title}</p>
                        </SwdAccordionTrigger>
                      </SwdAccordionHeader>
                      <SwdAccordionPanel>
                        <FooterNavLinks linkList={linkList} linkClassName={classes.link} />
                      </SwdAccordionPanel>
                    </SwdAccordionItem>
                  </SwdAccordionRoot>
                ))}
              </div>
            </>
          )}

          <Contact blok={blok} />
          <Divider className={classes.divider} />
          {blok.badges && <Badges badges={blok.badges} href={`/${getFullSlugFromMultilink(blok.link_badges) ?? ''}`} />}
        </div>
        <FooterMeta blok={blok} />
      </footer>
    </>
  );
};

export default Footer;
